import React, { useState, useEffect } from 'react';
import Input from '../Input';
import { AccentButton } from '../Button';
import { fetchCurrentUser, changePassword, requestEmailChange } from '../../../services/auth';
import { handleApiErrorWithMessages } from '../../../utils/apiErrorHandler';
import toast from 'react-hot-toast';

/* ── Reusable section card ───────────────────────────────────────────────── */
const SettingsCard = ({ title, description, children }) => (
  <div className="bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-2xl overflow-hidden mb-4">
    {(title || description) && (
      <div className="px-5 py-4 border-b border-[#ECECEC] dark:border-[#202020]">
        {title && (
          <h2 className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0] tracking-[-0.1px]">
            {title}
          </h2>
        )}
        {description && (
          <p className="mt-0.5 text-xs text-[#7B7B7B] dark:text-[#848484]">{description}</p>
        )}
      </div>
    )}
    {children}
  </div>
);

/* ── Read-only info row ──────────────────────────────────────────────────── */
const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between px-5 h-12">
    <span className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484]">{label}</span>
    <span className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0]">{value}</span>
  </div>
);

/* ── Inline edit section (expands below a header row) ────────────────────── */
const EditableSection = ({ label, displayValue, onSave, onCancel, isSaving, children }) => {
  const [open, setOpen] = useState(false);

  const handleCancel = () => {
    setOpen(false);
    onCancel?.();
  };

  const handleSave = async () => {
    const ok = await onSave?.();
    if (ok) setOpen(false);
  };

  return (
    <div className="border-b border-[#ECECEC] dark:border-[#202020] last:border-b-0">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 h-12">
        <span className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0]">{label}</span>
        <div className="flex items-center gap-3">
          {!open && displayValue && (
            <span className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484]">
              {displayValue}
            </span>
          )}
          {!open ? (
            <button
              onClick={() => setOpen(true)}
              className="text-xs font-semibold text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors"
            >
              Edit
            </button>
          ) : (
            <button
              onClick={handleCancel}
              className="text-xs font-semibold text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Expandable form */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          open ? 'max-h-[360px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-5 space-y-3">
          {children}
          <div className="flex justify-end pt-1">
            <AccentButton
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Save changes'}
            </AccentButton>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────────────────── */
const GeneralView = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Email change state
  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState(false);
  const [emailPasswordError, setEmailPasswordError] = useState('');
  const [newEmailError, setNewEmailError] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const userData = await fetchCurrentUser();
        setUser(userData);
      } catch {
        // silently fail
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const formatRegisterDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  /* Email save handler — returns true on success so EditableSection closes */
  const handleSaveEmail = async () => {
    setEmailPasswordError('');
    setNewEmailError('');

    if (!emailPassword.trim()) { setEmailPasswordError('Password is required'); return false; }
    if (!newEmail.trim()) { setNewEmailError('New email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) { setNewEmailError('Invalid email format'); return false; }

    setIsRequestingEmailChange(true);
    try {
      await requestEmailChange(emailPassword, newEmail);
      toast.success('Confirmation email sent. Please check your inbox.');
      setEmailPassword('');
      setNewEmail('');
      return true;
    } catch (error) {
      const { generalError, fieldErrors } = handleApiErrorWithMessages(error);
      if (fieldErrors.password) setEmailPasswordError(fieldErrors.password);
      if (fieldErrors.new_email) setNewEmailError(fieldErrors.new_email);
      if (generalError && !Object.keys(fieldErrors).length) toast.error(generalError);
      return false;
    } finally {
      setIsRequestingEmailChange(false);
    }
  };

  const handleCancelEmail = () => {
    setEmailPassword('');
    setNewEmail('');
    setEmailPasswordError('');
    setNewEmailError('');
  };

  /* Password save handler */
  const handleSavePassword = async () => {
    setCurrentPasswordError('');
    setNewPasswordError('');

    if (!currentPassword.trim()) { setCurrentPasswordError('Current password is required'); return false; }
    if (!newPassword.trim()) { setNewPasswordError('New password is required'); return false; }
    if (newPassword.length < 6) { setNewPasswordError('Must be at least 6 characters'); return false; }

    setIsChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      return true;
    } catch (error) {
      const { generalError, fieldErrors } = handleApiErrorWithMessages(error);
      if (fieldErrors.currentPassword) setCurrentPasswordError(fieldErrors.currentPassword);
      if (fieldErrors.newPassword) setNewPasswordError(fieldErrors.newPassword);
      if (generalError && !Object.keys(fieldErrors).length) toast.error(generalError);
      return false;
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelPassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setCurrentPasswordError('');
    setNewPasswordError('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#121212] dark:border-[#D0D0D0]" />
      </div>
    );
  }

  return (
    <>
      {/* Account card */}
      <SettingsCard title="Account" description="Manage your email address and password.">
        <EditableSection
          label="Email"
          displayValue={user?.email}
          onSave={handleSaveEmail}
          onCancel={handleCancelEmail}
          isSaving={isRequestingEmailChange}
        >
          <Input
            type="password"
            placeholder="Current password"
            value={emailPassword}
            onChange={(e) => { setEmailPassword(e.target.value); setEmailPasswordError(''); }}
            showPasswordToggle
            error={emailPasswordError}
            disabled={isRequestingEmailChange}
          />
          <Input
            type="email"
            placeholder="New email address"
            value={newEmail}
            onChange={(e) => { setNewEmail(e.target.value); setNewEmailError(''); }}
            error={newEmailError}
            disabled={isRequestingEmailChange}
          />
        </EditableSection>

        <EditableSection
          label="Password"
          displayValue="••••••••"
          onSave={handleSavePassword}
          onCancel={handleCancelPassword}
          isSaving={isChangingPassword}
        >
          <Input
            type="password"
            placeholder="Current password"
            value={currentPassword}
            onChange={(e) => { setCurrentPassword(e.target.value); setCurrentPasswordError(''); }}
            showPasswordToggle
            error={currentPasswordError}
            disabled={isChangingPassword}
          />
          <Input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(''); }}
            showPasswordToggle
            error={newPasswordError}
            disabled={isChangingPassword}
          />
        </EditableSection>
      </SettingsCard>

      {/* Meta card */}
      <SettingsCard title="Account info">
        <InfoRow label="Member since" value={formatRegisterDate(user?.created_at)} />
      </SettingsCard>
    </>
  );
};

export default GeneralView;
